import React from "react";
import { Route, Routes, BrowserRouter } from "react-router-dom";
import { History } from "history";
import { ConnectedProps, connect } from "react-redux";

import collectionRoutes from "us.collection/routes";
import Common from "us.common";
import { ConfigurationProvider } from "us.common/ConfigurationContext";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { SignalRProvider } from "us.common/SignalRProvider";
import { RootState } from "us.helper/types";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { FeatureFlagProvider } from "us.common/FeatureFlagContext";
import { useAuth } from "us.helper/hooks/useAuth";
import { Loader, LoginRedirect } from "us.common/components";
import { DrawerProvider } from "us.common/context";

const { Page404, AppLayout, ConfigProvider } = Common.Components;

type AppProps = {
  history: History;
};

const App: React.FC<AppProps & PropsFromRedux> = ({
  caseNo,
  currentLanguage,
}) => {
  const { isAuthenticated, loading, isInitialized } = useAuth();

  // While authentication state is being determined, never show the unauthenticated UI
  if (!isInitialized) {
    return <Loader />;
  }

  // If user not authenticated after initialization, present login redirect
  if (!isAuthenticated) {
    return (
      <UnauthenticatedTemplate>
        <BrowserRouter
          future={{
            v7_relativeSplatPath: true,
          }}
        >
          <LoginRedirect />
        </BrowserRouter>
      </UnauthenticatedTemplate>
    );
  }

  return (
    <AuthenticatedTemplate>
      <BrowserRouter
        future={{
          v7_relativeSplatPath: true,
        }}
      >
        <ConfigurationProvider>
          <FeatureFlagProvider>
            <SignalRProvider>
              <DrawerProvider>
                <AppLayout>
                  <ConfigProvider locale={currentLanguage}>
                    <DndProvider backend={HTML5Backend}>
                      <Routes>
                        {collectionRoutes(caseNo)}
                        <Route path="*" element={<Page404 />} />
                      </Routes>
                    </DndProvider>
                  </ConfigProvider>
                </AppLayout>
              </DrawerProvider>
            </SignalRProvider>
          </FeatureFlagProvider>
        </ConfigurationProvider>
      </BrowserRouter>
    </AuthenticatedTemplate>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    caseNo: state.case.caseNo,
    currentLanguage: state.common.currentLanguage,
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(App);
